import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Button, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Modal } from '@app/ui/modal';
import { Alert } from '@app/ui/alert';
import { error as toastError, success as toastSuccess } from '@app/snackbars';

import { RULE_SET_VERSION_DISCARD_DRAFT, RULE_SET_VERSION_GET_ONE } from '../gql';
import { RuleSetVersion } from '../types';
import { getGraphQLErrorMessage } from '../utils';

export interface DiscardDraftDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly version: RuleSetVersion;
  readonly parentVersionId: number | null;
  readonly ruleCount: number;
  readonly onDiscarded: () => void;
}

// Confirmation for the discard path (PO ask): a concrete brief on what is lost, not a generic "Are
// you sure?". The client-side diff preview is gone with `diffSummary` (SPEC D9) — what remains is
// what the discard actually does: this draft's rows go, the published version stays live.
export const DiscardDraftDialog = ({
  open,
  onClose,
  version,
  parentVersionId,
  ruleCount,
  onDiscarded,
}: DiscardDraftDialogProps) => {
  const { t } = useTranslation('common');

  const { data: parentData } = useQuery<{ ruleSetVersionGetOne: RuleSetVersion | null }>(RULE_SET_VERSION_GET_ONE, {
    variables: { id: parentVersionId },
    skip: !parentVersionId,
  });

  const [discardDraft, { loading: discarding }] = useMutation<{ ruleSetVersionDiscardDraft: boolean }>(
    RULE_SET_VERSION_DISCARD_DRAFT,
  );

  const parentVersion = parentData?.ruleSetVersionGetOne?.version ?? null;

  const handleDiscard = async () => {
    try {
      await discardDraft({ variables: { input: { id: version.id } } });
      toastSuccess(t('rules.discard.successMessage', { version: version.version }));
      onDiscarded();
    } catch (e) {
      toastError(getGraphQLErrorMessage(e) ?? t('general.error.somethingWentWrong'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={t('rules.discard.dialogTitle', { family: t(`rules.family.${version.family}`), version: version.version })}
    >
      <Typography variant="body1">{t('rules.discard.explanation', { count: ruleCount })}</Typography>

      {parentVersion !== null && (
        <Typography variant="body1" sx={{ mt: 1 }}>
          {t('rules.discard.publishedStaysLive', { version: parentVersion })}
        </Typography>
      )}

      <Alert severity="warning" sx={{ mt: 2 }}>
        {t('rules.discard.permanenceWarning')}
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={onClose}>{t('rules.discard.cancelButton')}</Button>
        <LoadingButton loading={discarding} variant="contained" color="error" onClick={handleDiscard}>
          {t('rules.discard.confirmButton')}
        </LoadingButton>
      </Box>
    </Modal>
  );
};
